import { Component, Input } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { batchAll, CallbackResult, CallbackResultStatus } from '../../../../../../utils';
import { CustomerService } from '../../../../../../services/customer/customer.service';

@Component({
  selector: 'app-bulk-send-allthenticate-invite-modal',
  templateUrl: './bulk-send-allthenticate-invite-modal.component.html',
  styleUrls: ['./bulk-send-allthenticate-invite-modal.component.css']
})
export class BulkSendAllthenticateInviteModalComponent {

  @Input() customerIds: number[];
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public errorMessage = '';
  public serverError: number = null;

  public loadingSave = false;
  public numberSaved = 0;

  constructor(
    public activeModal: NgbActiveModal,
    public customerService: CustomerService
  ) {}

  public handleSubmit() {
    this.errorMessage = '';
    this.loadingSave = true;

    this.numberSaved = 0;
    let successfulCount = 0;
    // Customers the server refused: those with no email or no barcode.
    let skippedCount = 0;

    const customerIdHandler = (customerId: number): Promise<void> => {
      // Stop early if an error message or server error is produced.
      if ((this.errorMessage !== '') || (this.serverError !== null)) {
        return Promise.resolve();
      }

      return this.customerService
        .sendAllthenticateInvite(customerId)
        .then(() => { successfulCount++; })
        .catch(error => {
          // 404 means the customer was removed between being selected and
          // this call. The list only offers active customers, so it is a
          // race rather than a normal outcome - but it must still be counted,
          // or they vanish from both tallies.
          if ((error.status === 400) || (error.status === 404)) {
            skippedCount++;
          } else if (error.status === 403) {
            this.errorMessage = 'You do not have permission to perform this operation.';
          } else if (error.status >= 500) {
            this.serverError = error.status;
          }
        })
        .finally(() => { this.numberSaved++; });
    };

    batchAll(
      4,
      this.customerIds,
      customerIdHandler
    )
      .then(() => {
        // Mirrors the short-circuit in customerIdHandler above: a 5xx aborts
        // the batch, so the modal must stay open showing its server-error
        // header rather than closing with a success message that hides both
        // the failure and the customers who were never reached.
        if ((this.errorMessage !== '') || (this.serverError !== null)) { return; }

        let successMessage = '';
        if (successfulCount > 0) {
          successMessage = 'Allthenticate invite sent to '
            + successfulCount.toString() + ' customer'
            + (successfulCount > 1 ? 's' : '') + '.';
        }
        if (skippedCount > 0) {
          successMessage += ' ' + skippedCount.toString() + ' customer'
            + (skippedCount > 1 ? 's' : '')
            + ' could not be invited, because they have no email address or'
            + ' barcode, or have since been removed.';
        }

        this.savedCallback({
          text: successMessage.trim(),
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSave = false; });
  }

  public saveButtonEnabled() {
    return !this.loadingSave;
  }

  public getLoadingProgressPercentageStr() {
    return Math.ceil((this.numberSaved / this.customerIds.length) * 100).toString();
  }

}
